import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { NavigationContainer, DefaultTheme, DarkTheme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createStackNavigator } from '@react-navigation/stack';
import { ColorSchemeName, StatusBar, Dimensions, Platform, Pressable, Image, ScrollView, FlatList, Linking, ActivityIndicator, View } from 'react-native';
import { LoginStackParamList, RootStackParamList } from './types';
// import { View, Text } from '@constants/Themed';
import LinkingConfiguration from './LinkingConfiguration';
import { useDispatch, useSelector } from 'react-redux';
import AuthModal from '../components/modals/ErrorModal/AuthModal';
import LoadingScreen from './screens/LoadingScreen/loadingScreen';

/** Lazy — Menu/DashStack is huge; do not parse it while Loading screen is active. */
const DashboardNavigator = React.lazy(() => import('./NavigationStacks/DashStack'));

function DashboardScreenLazy(props: object) {
  return (
    <Suspense
      fallback={(
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color="rgb(90,192,234)" />
        </View>
      )}
    >
      <DashboardNavigator {...props} />
    </Suspense>
  );
}
const PreviewScreen = React.lazy(() => import('./screens/TopLevel/PreviewScreen/PreviewScreen'));

function PreviewScreenLazy(props: object) {
  return (
    <Suspense
      fallback={(
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color="rgb(90,192,234)" />
        </View>
      )}
    >
      <PreviewScreen {...props} />
    </Suspense>
  );
}
import LoginScreen from './screens/TopLevel/LoginScreen/LoginScreen';
import SetPasswordScreen from './screens/TopLevel/SetPasswordScreen';
import styles from '../../stylesheet';
import Registration from "./screens/TopLevel/Registration";
import { USER_API_BASE_URL } from '@api/constant';

import RegisterModal from '../components/modals/RegisterModal/RegisterModal';
// import { StoreStackNavigator } from './NavigationStacks/StoreStack'; // Another potential app stack
import NotFoundScreen from './screens/TopLevel/NotFoundScreen';
import TCModal from '../components/modals/TCModal/TCModal';
// import FormScreen from './Form/FormScreen';
// import UserProfileScreen from './screens/IndexLevel/UserProfileDisplay/UserProfileScreen';
// import LiveReports from './screens/IndexLevel/LiveReportScreen/LiveReports';
import { CHANGE_LANGUAGE, CHANGE_THEME } from '@reduxLocal/actions/types';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { DefaultRootState } from '@reduxLocal/persistState'; // Ensure DefaultRootState is imported
import { trackUrlWithObjectId } from '@services/urlObjectIdTracker';
import { useColorScheme } from '../../App';
import { navigationRef } from './rootNavigation';
import { publishNavigationState } from './utils/routeReporter';
import CurrentRouteMarker from './Components/CurrentRouteMarker';
import LoginArtShell from './loginArt/LoginArtShell.web';
import GlassSplitOverlay from './loginArt/GlassSplitOverlay.web';

if (__DEV__) {
  require('./dev/navigationMirror');
}

// Conditional BottomSheet Provider - only use on native platforms
// On web, BottomSheetModalProvider is not available, so we just pass through children
const ConditionalBottomSheetProvider = ({ children }: { children: React.ReactNode }) => {
  // Since this is index.web.tsx, we know we're on web, so just return children
  // The native version would use the actual BottomSheetModalProvider
  return <>{children}</>;
};

// Create a Root Stack Navigator that will conditionally render the Auth or App flow
const RootStack = Platform.OS === 'web' 
  ? createStackNavigator<RootStackParamList>() 
  : createNativeStackNavigator<RootStackParamList>();

const Navigation = ({ colorScheme, hasUpdatePending }: { colorScheme: ColorSchemeName, hasUpdatePending: boolean }) => {
  
  const dispatch = useDispatch();
  const reduxTheme = themeSelector();
  const { colorScheme: webColorScheme } = useColorScheme();
  const insets = useSafeAreaInsets();
  const isLight = webColorScheme === 'light';
  const cardBg = isLight ? '#ffffff' : '#333333';

  
  // --- CRITICAL: Select authentication state from Redux ---
  const isAuthenticated = useSelector((state: DefaultRootState) => state.promiseData?.isAuthenticated || false); 
  const isLoadingAuth = useSelector((state: DefaultRootState) => state.promiseData?.loading || false);


  useEffect(() => {
    document.documentElement.style.setProperty('--bs-card-bg', cardBg);
  },[cardBg])

  // Mirror the web-native theme into Redux so existing components keep working.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (reduxTheme === isLight) return;
    dispatch({
      type: CHANGE_THEME,
      payload: { lightTheme: isLight, theme: webColorScheme },
    });
  }, [dispatch, isLight, reduxTheme, webColorScheme]);

  // Log authentication state changes for debugging
  useEffect(() => {
    console.log('🔐 Auth State Changed:', {
      isAuthenticated,
      isLoadingAuth,
      timestamp: new Date().toISOString()
    });
  }, [isAuthenticated, isLoadingAuth]);

  // Function to call the generate payment link API
  const generatePaymentLink = async (productId: string, promoCode?: string) => {
    try {
      const url = new URL(`${USER_API_BASE_URL}/payments/generate-payment-link`);
      url.searchParams.set('productId', productId);
      if (promoCode) {
        url.searchParams.set('promoCode', promoCode);
      }

      console.log('🌐 API Base URL:', USER_API_BASE_URL);
      console.log('🌐 Full API URL:', url.toString());

      const requestOptions = {
        method: 'GET',
        credentials: 'include' as RequestCredentials, // Include session cookies
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      };
      

      const response = await fetch(url.toString(), requestOptions);



      const responseText = await response.text();
      console.log('📡 Raw API Response:', responseText);

      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          console.log('✅ Parsed response data:', data);
          console.log('🔗 Checkout URL:', data.checkoutUrl);
          return data.checkoutUrl;
        } catch (parseError) {
          console.error('💥 JSON Parse Error:', parseError);
          console.error('📄 Response text that failed to parse:', responseText);
          return null;
        }
      } else {
        console.error('❌ API Request Failed:', {
          status: response.status,
          statusText: response.statusText,
          responseBody: responseText
        });
        return null;
      }
    } catch (error) {
      console.error('💥 Network/Fetch Error:', error);
      console.error('🔍 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return null;
    }
  };

  // Debug function for testing payment link generation
  useEffect(() => {
    if (__DEV__ && typeof window !== 'undefined') {
      // Add debug function to window for manual testing
      (window as any).debugPaymentLink = async (productId: string, code: string) => {
        console.log('🧪 Manual debug test started');
        console.log('🔍 Current auth state:', { isAuthenticated, isLoadingAuth });
        const result = await generatePaymentLink(productId, code);
        console.log('🧪 Debug result:', result);
        return result;
      };
      
      // Add function to check current URL params
      (window as any).checkUrlParams = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlParams.entries());
        console.log('🔍 Current URL params:', params);
        return params;
      };

      console.log('🛠️ Debug functions added to window:');
      console.log('   - window.debugPaymentLink(productId, code)');
      console.log('   - window.checkUrlParams()');
    }
  }, [isAuthenticated, isLoadingAuth]);



  // Check for productId and code in URL params when user is authenticated
  useEffect(() => {

    if (Platform.OS === 'web') {
      
      if (isAuthenticated) {
        
        if (!isLoadingAuth) {
          
          const urlParams = new URLSearchParams(window.location.search);
          const productId = urlParams.get('productId');
          
          // Check for both 'code' and 'promoCode' parameters
          const code = urlParams.get('code') || urlParams.get('promoCode');
          
          // Validate URL format
          if (window.location.search.includes('??')) {
            console.warn('⚠️ Invalid URL format detected - multiple question marks found');
            console.warn('💡 Expected format: ?productId=XXX&code=YYY');
            console.warn('💡 Current URL:', window.location.href);
          }

          if (productId && code) {
            generatePaymentLink(productId, code).then((checkoutUrl) => {
              if (checkoutUrl) {
                // Clean up URL parameters before redirecting
                const newUrl = new URL(window.location.href);
                newUrl.searchParams.delete('productId');
                newUrl.searchParams.delete('code');
                newUrl.searchParams.delete('promoCode');
                
                
                // Update browser history to clean URL without causing navigation
                window.history.replaceState({}, '', newUrl.toString());
                
                // Redirect to the checkout URL
                window.location.href = checkoutUrl;
              } else {
                console.error('❌ Failed to generate payment link - checkoutUrl is null/undefined');
              }
            }).catch((error) => {
              console.error('💥 Promise rejection in generatePaymentLink:', error);
            });
          } else {
          }
        } else {
        }
      } else {
      }
    } else {
    }
  }, [isAuthenticated, isLoadingAuth]);

  // Track URLs containing ObjectIds for context enrichment
  const shouldTrackUrls = isAuthenticated && !isLoadingAuth;
  const trackCurrentUrl = useCallback(() => {
    if (!shouldTrackUrls || typeof window === 'undefined' || !window.location.href) return;
    trackUrlWithObjectId(window.location.href, {
      source: 'navigation',
      authenticated: true,
    }).catch((error) => {
      console.warn('Failed to track URL:', error);
    });
  }, [shouldTrackUrls]);

  useEffect(() => {
    trackCurrentUrl();
  }, [trackCurrentUrl]);

  // Handle navigation when auth state changes
  // NOTE: On web, we let LoadingScreen handle initial navigation after session validation
  // This effect only handles subsequent auth state changes (e.g., after logout) on native platforms
  useEffect(() => {
    // Skip auto-navigation on web - LoadingScreen handles it after session validation
    if (Platform.OS === 'web') {
      return;
    }

    // On native platforms, handle navigation normally
    if (navigationRef.isReady() && !isLoadingAuth) {
      try {
        if (isAuthenticated) {
          navigationRef.navigate('Dashboard');
        } else {
          navigationRef.navigate('Auth');
        }
      } catch (error) {
        console.error('Navigation error:', error);
      }
    }
  }, [isAuthenticated, isLoadingAuth]);

  const navTheme = useMemo(() => ({
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      card: 'black',
      view: 'white',
      notification: 'white',
      text: 'black',
    },
  }), []);

  const darkTheme = useMemo(() => ({
    ...DarkTheme,
    colors: {
      ...DarkTheme.colors,
      text: 'white',
    }
  }), []);

  return (
    <NavigationContainer 
      ref={navigationRef}
      documentTitle={{
        formatter: (options, route) => {
          // Use explicit screen title if provided, otherwise fallback to route name
          const pageTitle = options?.title ?? route.name;
          return `SmartSaaS - ${pageTitle}`;
        },
      }}
      linking={LinkingConfiguration}
      theme={colorScheme === 'dark' ? darkTheme : navTheme}
      onReady={() => {
        if (navigationRef.isReady()) {
          publishNavigationState(navigationRef.getRootState());
        }
      }}
      onStateChange={(state) => {
        publishNavigationState(state);
        // Linking writes the new URL after the state change commits.
        setTimeout(trackCurrentUrl, 0);
      }}
    >
      <ConditionalBottomSheetProvider>
        <RootStack.Navigator 
          id="RootStack"
          initialRouteName={Platform.OS === 'web' ? "Loading" : (isLoadingAuth ? "Loading" : isAuthenticated ? "Dashboard" : "Auth")}
          screenOptions={{ headerShown: false, gestureEnabled: false }}
        >
          <RootStack.Screen name="Loading" component={LoadingScreen} />
          <RootStack.Screen name="Dashboard" component={DashboardScreenLazy} />
          <RootStack.Screen name="Auth" component={LoginAuthStack} />
          <RootStack.Screen name="NotFound" component={NotFoundScreen} />
        </RootStack.Navigator>

        {/* Survives Auth → Dashboard reset so login curtain / glass morph can finish. */}
        <LoginArtShell />
        <GlassSplitOverlay />
        <CurrentRouteMarker />
        <TCModal/>
        <RegisterModal />
        <AuthModal />
      </ConditionalBottomSheetProvider>
    </NavigationContainer>
  );
};

// --- NEW: Create a separate Login/Auth Stack ---
// This will contain all your authentication-related screens
const LoginAuthStack = () => {
  const LoginAuthStackInternal = Platform.OS === 'web'
    ? createStackNavigator<LoginStackParamList>()
    : createNativeStackNavigator<LoginStackParamList>();

  // Determine initial route based on URL - use useEffect to handle URL changes
  const [initialRoute, setInitialRoute] = React.useState(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname.includes('/learn')) {
        return 'Learn';
      }
      if (pathname.includes('/set-password')) {
        return 'SetPassword';
      }
      if (pathname.includes('/registration')) {
        return 'Registration';
      }
      if (pathname.includes('/preview')) {
        return 'Preview';
      }
    }
    return 'Login';
  });

  // Update route when URL changes
  React.useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      if (pathname.includes('/learn')) {
        setInitialRoute('Learn');
      } else if (pathname.includes('/set-password')) {
        setInitialRoute('SetPassword');
      } else if (pathname.includes('/registration')) {
        setInitialRoute('Registration');
      } else if (pathname.includes('/preview')) {
        setInitialRoute('Preview');
      } else {
        setInitialRoute('Login');
      }
    }
  }, []);

  // Clean URL on mount if needed
  React.useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      const hasLearningModeParam = searchParams.has('learningMode');
      
      // If we're on /login and have learningMode param, remove it
      if (pathname.includes('/login') && hasLearningModeParam) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('learningMode');
        newUrl.searchParams.delete('learning');
        newUrl.searchParams.delete('learn');
        window.history.replaceState({}, '', newUrl.toString());
      }
    }
  }, []);

  return (
    <LoginAuthStackInternal.Navigator
      initialRouteName={initialRoute}
      screenOptions={{ headerShown: false, gestureEnabled: false }}>
      <LoginAuthStackInternal.Screen 
        name="Login" 
        component={LoginScreen} 
        options={{ freezeOnBlur: true }}
      />
      <LoginAuthStackInternal.Screen 
        name="Learn" 
        component={LoginScreen} 
        options={{ freezeOnBlur: true }}
        listeners={{
          focus: () => {
            // Ensure URL stays as /learn when Learn screen is focused
            if (Platform.OS === 'web' && typeof window !== 'undefined') {
              const newUrl = new URL(window.location.href);
              if (newUrl.pathname !== '/learn') {
                newUrl.pathname = '/learn';
                // Remove any query params
                newUrl.search = '';
                window.history.replaceState({}, '', newUrl.toString());
              }
            }
          },
        }}
      />
      <LoginAuthStackInternal.Screen name="Registration" component={Registration} options={{ freezeOnBlur: true }} />
      <LoginAuthStackInternal.Screen
        name="SetPassword"
        component={SetPasswordScreen}
        options={{ freezeOnBlur: true }}
      />
      <LoginAuthStackInternal.Screen name="Preview" component={PreviewScreenLazy} />

      {/* Add other authentication screens here if you have them */}
      {/* <LoginAuthStackInternal.Screen name="Register" component={RegisterScreen} /> */}
      {/* <LoginAuthStackInternal.Screen name="ForgotPassword" component={ForgotPasswordScreen} /> */}
    </LoginAuthStackInternal.Navigator>
  );
};

export default Navigation;
